import axios from "axios";
import { Edit2, Save, Trash2, X } from "lucide-react";
import React, { useEffect, useState } from "react";

// Create an axios instance with baseURL configured
const api = axios.create({
  baseURL: "https://galaxy-alpr-be.onrender.com",
});

interface PlateStatus {
  plate: string;
  type: "blacklist" | "whitelist";
  timestamp: string;
}

interface AccessListTabProps {
  plates: { plate: string; type: "blacklist" | "whitelist" }[];
  newPlate: string;
  setNewPlate: (v: string) => void;
  newType: "blacklist" | "whitelist";
  setNewType: (v: "blacklist" | "whitelist") => void;
  handleAddPlate: () => void;
  handleRemovePlate: (plate: string) => void;
}

const AccessListTab: React.FC<AccessListTabProps> = ({
  plates,
  newPlate,
  setNewPlate,
  newType,
  setNewType,
  handleAddPlate,
  handleRemovePlate,
}) => {
  const [plateStatuses, setPlateStatuses] = useState<PlateStatus[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingPlate, setEditingPlate] = useState<string | null>(null);
  const [editValue, setEditValue] = useState<string>("");

  useEffect(() => {
    // Fetch plate statuses from the API
    const fetchPlateStatuses = async () => {
      setLoading(true);
      setError(null);
      try {
        const response = await api.get("/api/plate-status");
        console.log("Plate status response:", response.data);

        if (response.data.status === "success") {
          setPlateStatuses(response.data.plate_statuses || []);
        } else {
          setError(
            "Failed to load plate statuses: " +
            (response.data.detail || "Unknown error")
          );
        }
      } catch (err: any) {
        console.error("Error fetching plate statuses:", err);
        setError(
          `Error fetching plate statuses: ${err.response?.data?.detail || err.message || "Unknown error"
          }`
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPlateStatuses();
  }, []);

  // Define actual API-based add and remove handlers
  const handleAddPlateToApi = async () => {
    if (!newPlate.trim()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await api.post("/api/plate-status", {
        plate_text: newPlate,
        status_type: newType,
      });

      console.log("Add plate response:", response.data);

      // If successful, add to local state
      setPlateStatuses([
        ...plateStatuses,
        {
          plate: newPlate,
          type: newType,
          timestamp: new Date().toISOString(),
        },
      ]);

      // Clear input
      setNewPlate("");
    } catch (err: any) {
      console.error("Failed to add plate:", err);
      setError(
        `Failed to add plate: ${err.response?.data?.detail || err.message || "Unknown error"
        }`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleRemovePlateFromApi = async (plate: string) => {
    setLoading(true);
    setError(null);

    try {
      const response = await api.delete(
        `/api/plate-status/${encodeURIComponent(plate)}`
      );
      console.log("Remove plate response:", response.data);

      // If successful, remove from local state
      setPlateStatuses(plateStatuses.filter((item) => item.plate !== plate));
    } catch (err: any) {
      console.error("Failed to remove plate:", err);
      setError(
        `Failed to remove plate: ${err.response?.data?.detail || err.message || "Unknown error"
        }`
      );
    } finally {
      setLoading(false);
    }
  };

  // Start editing a plate
  const startEditingPlate = (plate: string) => {
    setEditingPlate(plate);
    setEditValue(plate);
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingPlate(null);
  };

  // Save edited plate
  const saveEditedPlate = async (originalPlate: string, plateType: "blacklist" | "whitelist") => {
    if (!editValue.trim() || editValue === originalPlate) {
      cancelEditing();
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // First remove the original plate
      await api.delete(`/api/plate-status/${encodeURIComponent(originalPlate)}`);

      // Then add the new plate with the same status type
      const response = await api.post("/api/plate-status", {
        plate_text: editValue,
        status_type: plateType,
      });

      console.log("Update plate response:", response.data);

      // Update local state
      setPlateStatuses(
        plateStatuses.map((item) =>
          item.plate === originalPlate
            ? { ...item, plate: editValue }
            : item
        )
      );

      // Exit edit mode
      setEditingPlate(null);
    } catch (err: any) {
      console.error("Failed to update plate:", err);
      setError(
        `Failed to update plate: ${err.response?.data?.detail || err.message || "Unknown error"}`
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h2 className="text-xl font-bold text-gray-800 mb-4">
        Blacklist/Whitelist Vehicle Plates
      </h2>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 relative">
          <span className="block sm:inline">{error}</span>
          <button
            onClick={() => setError(null)}
            className="absolute top-0 right-0 px-4 py-3"
          >
            <X size={18} />
          </button>
        </div>
      )}
      <div className="mb-6 bg-white p-4 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-md font-medium text-gray-700 mb-3">Add New Plate</h3>
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Enter plate number"
              value={newPlate}
              onChange={(e) => setNewPlate(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div className="w-40">
            <select
              value={newType}
              onChange={(e) =>
                setNewType(e.target.value as "blacklist" | "whitelist")
              }
              className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="blacklist">Blacklist</option>
              <option value="whitelist">Whitelist</option>
            </select>
          </div>
          <div>
            <button
              onClick={handleAddPlateToApi}
              className="w-full md:w-auto bg-blue-500 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-blue-300 disabled:cursor-not-allowed"
              disabled={loading || !newPlate.trim()}
            >
              {loading ? "Adding..." : "Add Plate"}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plate Number
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading && plateStatuses.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-6 py-4 text-center text-sm text-gray-500">
                    <div className="flex items-center justify-center">
                      <div className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-solid border-blue-500 border-r-transparent"></div>
                      <span className="ml-2">Loading plates...</span>
                    </div>
                  </td>
                </tr>
              ) : plateStatuses.length === 0 ? (
                <tr>
                  <td colSpan={3} className="px-6 py-4 text-center">
                    <div className="flex flex-col items-center justify-center py-6 text-gray-500">
                      <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                      </svg>
                      <h3 className="mt-2 text-sm font-medium text-gray-900">No plates added</h3>
                      <p className="mt-1 text-sm text-gray-500">Add a new plate to get started.</p>
                    </div>
                  </td>
                </tr>
              ) : (
                plateStatuses.map((item, idx) => (
                  <tr key={item.plate + idx} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {editingPlate === item.plate ? (
                        <input
                          type="text"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="w-full border border-gray-300 rounded-md shadow-sm py-1 px-2 focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                          autoFocus
                        />
                      ) : (
                        item.plate
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${item.type === "blacklist"
                          ? "bg-red-100 text-red-800"
                          : "bg-green-100 text-green-800"
                          }`}
                      >
                        {item.type.charAt(0).toUpperCase() + item.type.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-2">
                      {editingPlate === item.plate ? (
                        <>
                          <button
                            onClick={() => saveEditedPlate(item.plate, item.type)}
                            className="text-green-600 hover:text-green-900 bg-green-50 p-1.5 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500"
                            disabled={loading}
                            title="Save"
                          >
                            <Save size={16} />
                          </button>
                          <button
                            onClick={cancelEditing}
                            className="text-gray-600 hover:text-gray-900 bg-gray-50 p-1.5 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-500"
                            disabled={loading}
                            title="Cancel"
                          >
                            <X size={16} />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => startEditingPlate(item.plate)}
                            className="text-blue-600 hover:text-blue-900 bg-blue-50 p-1.5 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            disabled={loading || editingPlate !== null}
                            title="Edit"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button
                            onClick={() => handleRemovePlateFromApi(item.plate)}
                            className="text-red-600 hover:text-red-900 bg-red-50 p-1.5 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                            disabled={loading || editingPlate !== null}
                            title="Delete"
                          >
                            <Trash2 size={16} />
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AccessListTab;